import { Translate } from "src/utils/emr/worklistTimingHelper";

import {
  EMRHubConnectionSourceKind,
  EMRProblemDetailsType,
  EMRProblemType,
} from "src/services/emr/types";

const BYTES_PER_GIGABYTE = 1024 * 1024 * 1024;

const describeAddress = (details: EMRProblemDetailsType) =>
  [
    details.host && details.port !== undefined
      ? `${details.host}:${details.port}`
      : details.host,
    details.remoteAeTitle && `(${details.remoteAeTitle})`,
  ]
    .filter(Boolean)
    .join(" ");

const withReason = (text: string, details: EMRProblemDetailsType) =>
  details.reason ? `${text} (${details.reason})` : text;

const describeSkew = (seconds: number, t: Translate) => {
  const minutes = Math.max(1, Math.round(Math.abs(seconds) / 60));
  return seconds > 0
    ? t("{{minutes}} min ahead", { minutes })
    : t("{{minutes}} min behind", { minutes });
};

const SETTING_RULES: Record<string, string> = {
  required: "{{setting}} is missing.",
  tooLong: "{{setting}} is too long.",
  outOfRange: "{{setting}} is out of range.",
  unknownValue: "{{setting}} has a value the hub doesn't know.",
  invalidFormat: "{{setting}} is not in the right format.",
  empty: "{{setting}} is empty.",
  notSupported: "{{setting}} is not supported by this hub version.",
  malformed: "The settings could not be read.",
};

const describeSettings = (details: EMRProblemDetailsType, t: Translate) => {
  const rule = SETTING_RULES[details.rule ?? ""];
  const text = rule
    ? t(rule, { setting: details.setting ?? t("A setting") })
    : t("The settings are not valid.");

  return `${withReason(text, details)} ${t(
    "Fix them here; the connection is paused until then."
  )}`;
};

export const describeProblem = (
  problem: Pick<EMRProblemType, "code" | "details">,
  sourceKind: EMRHubConnectionSourceKind | undefined,
  t: Translate
) => {
  const details = problem.details ?? {};
  const address = describeAddress(details);
  const path = details.path ?? "";
  const isWritten = sourceKind === "ExportFolder";

  switch (problem.code) {
    case "host_unreachable":
    case "worklist_unreachable":
      return t(
        "Can't reach {{address}}. Check it's on and the address is right.",
        {
          address,
        }
      );
    case "connection_timed_out":
      return t(
        "{{address}} didn't answer. Check its DICOM service and the address.",
        { address }
      );
    case "association_rejected":
      return withReason(
        t(
          "{{address}} refused the hub. Allow AE {{aeTitle}} from the hub's IP.",
          {
            address,
            aeTitle: details.localAeTitle ?? "",
          }
        ),
        details
      );
    case "ae_title_rejected":
      return t(
        "{{address}} doesn't know the hub. Register AE {{aeTitle}} with the hub's IP and check AE {{remoteAeTitle}}.",
        {
          address,
          aeTitle: details.localAeTitle ?? "",
          remoteAeTitle: details.remoteAeTitle ?? "",
        }
      );
    case "association_aborted":
      return t("{{address}} dropped the connection. Check the archive's log.", {
        address,
      });
    case "request_failed":
      return withReason(
        t(
          "{{address}} answered with an error. Check AE {{aeTitle}} may query there.",
          {
            address,
            aeTitle: details.localAeTitle ?? "",
          }
        ),
        details
      );
    case "store_rejected":
      return withReason(
        t(
          "{{address}} refused a report. Check AE {{aeTitle}} may store there.",
          {
            address,
            aeTitle: details.localAeTitle ?? "",
          }
        ),
        details
      );
    case "folder_not_found":
      return t(
        "Folder {{path}} not found. Check the path and that the share is online.",
        { path }
      );
    case "folder_access_denied":
      return isWritten
        ? t(
            "No write access to {{path}}. Give the hub's service account access.",
            {
              path,
            }
          )
        : t(
            "No read access to {{path}}. Give the hub's service account access.",
            {
              path,
            }
          );
    case "folder_unavailable":
      return withReason(
        isWritten
          ? t("Couldn't write to {{path}}. Check the share is online.", {
              path,
            })
          : t("Couldn't read {{path}}. Check the share is online.", { path }),
        details
      );
    case "port_in_use":
      return t(
        "Port {{port}} is already in use on the hub PC. Pick another port.",
        {
          port: details.port ?? "",
        }
      );
    case "port_access_denied":
      return t("Windows blocked port {{port}}. Check the hub PC's firewall.", {
        port: details.port ?? "",
      });
    case "listener_bind_failed":
      return withReason(
        t(
          "Couldn't listen on port {{port}}. Check the port and the firewall.",
          {
            port: details.port ?? "",
          }
        ),
        details
      );
    case "configuration_invalid":
      return describeSettings(details, t);
    case "unexpected_error":
      return withReason(
        t("Unexpected error. Check the hub PC's Event Log."),
        details
      );
    case "low_disk_space":
      return t(
        "Only {{free}} GB left on {{volume}}. Free up space or new exams stop.",
        {
          free: ((details.freeBytes ?? 0) / BYTES_PER_GIGABYTE).toFixed(1),
          volume: details.volume ?? "",
        }
      );
    case "clock_skew":
      return t("The hub PC's clock is {{skew}}. Check Windows Time.", {
        skew: describeSkew(details.clockSkewSeconds ?? 0, t),
      });
    case "archive_folder_not_writable":
      return t(
        "Can't move processed files to {{path}}. Give the hub's service account access.",
        { path }
      );
    case "hub_offline":
      return details.lastContactAt
        ? t("No contact since {{date}}. Check the hub PC is on and online.", {
            date: new Date(details.lastContactAt).toLocaleString(),
          })
        : t("No contact with the hub. Check the hub PC is on and online.");
    default:
      return withReason(
        t("Something went wrong ({{code}}).", { code: problem.code }),
        details
      );
  }
};
